import React from 'react';
import Word from 'saur_ui_library/src/Atoms/Word/Word';
import {
  AreaChart,
  BarChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { zoomButton } from '../../utils/helpers/view/ecpDetailHelper';

interface EcpChartsComponentRechartsProps {
  display: string;
  height: string;
  sanitaryWater: any[];
  mode: string;
  title: string;
  colorsLegend: string[];
  pluvioData: any[];
  piezoData?: any[];
  pluvioMax: number;
  piezoMax?: number;
  dataMaxValue: number;
  axisYLabel: string;
  axisXLabel: string;
  axisYLabelPluvio: string;
  handleZoomIn(): void;
  handleZoomOut(): void;
  dataSelected?: any;
  stepReferenceFlow?: number | null;
  chartStartDate?: Date | null;
  chartEndDate?: Date | null;
}

const formatTooltipValue = (value?: number): string => {
  return Number.isFinite(value)
    ? value!.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
    : 'NR';
};

const getSeriesUnit = (dataKey?: string): string => {
  if (dataKey === 'pluvio') return 'mm';
  if (dataKey === 'piezo') return 'm';
  if (dataKey?.startsWith('series_')) return 'm³';
  return '';
};

const formatTooltipValueWithUnit = (value: number | undefined, unit: string): string => {
  const formatted = formatTooltipValue(value);
  return formatted === 'NR' || !unit ? formatted : `${formatted} ${unit}`;
};

const DailyLegendWithReference = ({
  payload,
  showReference,
  referenceLabel,
}: {
  payload?: any[];
  showReference: boolean;
  referenceLabel: string;
}) => {
  const basePayload = payload ?? [];
  const legendItems = showReference
    ? [...basePayload, { value: referenceLabel, color: '#E63946', type: 'line' }]
    : basePayload;

  return (
    <ul
      className="recharts-default-legend"
      style={{
        padding: 0,
        margin: 0,
        textAlign: 'center',
      }}
    >
      {legendItems.map((entry: any, index: number) => {
        const isLine = entry?.type === 'line';
        return (
          <li
            key={`legend-item-${index}`}
            className="recharts-legend-item"
            style={{
              display: 'inline-block',
              marginRight: 10,
            }}
          >
            <svg className="recharts-surface" width="18" height="18" viewBox="0 0 32 32" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }}>
              {isLine ? (
                <path d="M0,16h32" stroke={entry.color} strokeWidth="7" fill="none" />
              ) : (
                <path d="M0,4h32v24h-32z" fill={entry.color} stroke={entry.color} strokeWidth="2" />
              )}
            </svg>
            <span className="recharts-legend-item-text" style={{ color: '#666', fontSize: 12 }}>
              {entry.value}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const RotatedXAxisTick = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="end" fill="#000000" fontSize={11} transform="rotate(-30)">
        {payload.value}
      </text>
    </g>
  );
};

const formatMonthLabel = (dateValue: Date): string => {
  const month = `${dateValue.getMonth() + 1}`.padStart(2, '0');
  const year = dateValue.getFullYear();
  return `${month}/${year}`;
};

const CustomTooltip = (
  props: any,
  seriesConfig: { dataKey: string; name: string; color: string }[],
  originalData?: Map<number, any>,
  mode?: string,
) => {
  const { active, payload, label } = props;

  if (!active) return null;

  // Resolve tooltip date/timestamp from the payload first (works for both time and category axes),
  // falling back to the axis label so gap points (no series has a value) still show a tooltip
  const payloadPoint = payload && payload[0]?.payload;
  const payloadDate = payloadPoint?.x ? new Date(payloadPoint.x) : null;
  const labelDate = label !== undefined && label !== null
    ? (label instanceof Date ? label : new Date(label))
    : null;
  const tooltipDate = payloadDate ?? labelDate;
  if (!tooltipDate) return null;
  const ts = payloadPoint?.timestamp ?? tooltipDate.getTime();
  const originalPoint = originalData?.get(ts);

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid #D7DBE7',
        borderRadius: '6px',
        boxShadow: '0 8px 16px rgba(18, 26, 49, 0.12)',
        padding: '8px 10px',
        fontSize: '11px',
        lineHeight: 1.4,
        color: '#232323',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '6px', textAlign: 'center' }}>
        {mode === 'bar'
          ? formatMonthLabel(tooltipDate)
          : tooltipDate.toLocaleDateString('fr-FR')}
      </div>
      {/* Always show every series for this date, showing "NR" for null/missing values */}
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {seriesConfig.map((series, index) => {
            const rawValue = originalPoint?.[`${series.dataKey}_raw`];
            const unit = getSeriesUnit(series.dataKey);
            const displayValue = rawValue === null || rawValue === undefined
              ? 'NR'
              : formatTooltipValueWithUnit(rawValue, unit);
            const isSecondarySeries = series.dataKey === 'pluvio' || series.dataKey === 'piezo';
            const topPadding = isSecondarySeries ? '10px' : '2px';

            return (
              <tr key={`tooltip-${index}`}>
                <td style={{ padding: `${topPadding} 6px 2px 0`, verticalAlign: 'middle' }}>
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: series.color,
                      display: 'inline-block',
                    }}
                  />
                </td>
                <td style={{ padding: `${topPadding} 10px 2px 0`, textAlign: 'left' }}>{series.name}</td>
                <td style={{ padding: `${topPadding} 0 2px`, textAlign: 'right', fontWeight: 700 }}>{displayValue}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const EcpChartsComponentRecharts = (props: EcpChartsComponentRechartsProps) => {
  const chartRef = React.useRef<HTMLDivElement | null>(null);

  // Drag-to-zoom state (equivalent to the previous library's VictoryZoomVoronoiContainer):
  // the user drags across the daily chart to select a date range, releasing zooms the x-axis
  // into that range; a "reset zoom" control brings back the full range.
  const [zoomDomain, setZoomDomain] = React.useState<{ left: number; right: number } | null>(null);
  const [refAreaLeft, setRefAreaLeft] = React.useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = React.useState<number | null>(null);

  const handleChartMouseDown = (chartEvent: any) => {
    if (!chartEvent || chartEvent.activeLabel === undefined || chartEvent.activeLabel === null) return;
    setRefAreaLeft(new Date(chartEvent.activeLabel).getTime());
  };

  const handleChartMouseMove = (chartEvent: any) => {
    if (refAreaLeft === null || !chartEvent || chartEvent.activeLabel === undefined || chartEvent.activeLabel === null) return;
    setRefAreaRight(new Date(chartEvent.activeLabel).getTime());
  };

  const handleChartMouseUp = () => {
    if (refAreaLeft === null || refAreaRight === null || refAreaLeft === refAreaRight) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    setZoomDomain({
      left: Math.min(refAreaLeft, refAreaRight),
      right: Math.max(refAreaLeft, refAreaRight),
    });
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const resetZoom = () => {
    setZoomDomain(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };


  // Merge all data into a single array indexed by date.
  // Recomputed only when the underlying data/mode/range actually change, instead of on every
  // render (e.g. hover, tab switches, unrelated parent re-renders), which was the main source
  // of the sluggishness compared to the previous charting library.
  const { sortedData, originalDataMap } = React.useMemo(() => {
    // Normalize any date/time to a local-midnight timestamp. Series merged here can come from
    // sources with inconsistent time-of-day/timezone info (e.g. piezo measures); without this,
    // points that should land on the same day end up as separate map entries interleaved with
    // "no data" filler days, which breaks the Line's path into invisible single-point segments.
    const getDayTimestamp = (value: any) => {
      const d = new Date(value);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    };

    const dataMap = new Map<number, any>();
    const originalDataMap = new Map<number, any>();

    // Add sanitary water data
    if (props.sanitaryWater && props.sanitaryWater.length > 0) {
      props.sanitaryWater.forEach((series: any[], seriesIndex: number) => {
        series.forEach((point: any) => {
          const ts = getDayTimestamp(point.x);
          if (!dataMap.has(ts)) {
            const date = new Date(point.x);
            dataMap.set(ts, {
              x: date,
              timestamp: ts,
              xLabel: formatMonthLabel(date),
            });
            originalDataMap.set(ts, { timestamp: ts });
          }
          const entry = dataMap.get(ts)!;
          const originalEntry = originalDataMap.get(ts)!;
          // For bar charts, convert null to 0; for area charts, keep null for gaps
          const value = props.mode === 'bar' ? (point.y ?? 0) : point.y;
          entry[`series_${seriesIndex}`] = value;
          originalEntry[`series_${seriesIndex}_raw`] = point.y;
        });
      });
    }

    // Add pluvio data
    if (props.pluvioData && props.pluvioData.length > 0) {
      props.pluvioData.forEach((point: any) => {
        const ts = getDayTimestamp(point.x);
        if (!dataMap.has(ts)) {
          const date = new Date(point.x);
          dataMap.set(ts, {
            x: date,
            timestamp: ts,
            xLabel: formatMonthLabel(date),
          });
          originalDataMap.set(ts, { timestamp: ts });
        }
        const pluvioValue = point.y;
        dataMap.get(ts)!.pluvio = pluvioValue;
        originalDataMap.get(ts)!.pluvio_raw = point.y;
      });
    }

    // Add piezo data
    if (props.piezoData && props.piezoData.length > 0) {
      props.piezoData.forEach((point: any) => {
        const ts = getDayTimestamp(point.x);
        if (!dataMap.has(ts)) {
          const date = new Date(point.x);
          dataMap.set(ts, {
            x: date,
            timestamp: ts,
            xLabel: formatMonthLabel(date),
          });
          originalDataMap.set(ts, { timestamp: ts });
        }
        const piezoValue = point.y;
        dataMap.get(ts)!.piezo = piezoValue;
        originalDataMap.get(ts)!.piezo_raw = point.y;
      });
    }

    // Fill in missing days across the full range so hovering on gap zones (where every
    // series is null, including a range with no data at all) still shows a tooltip with "NR"
    if (props.mode === 'area') {
      const knownTimestamps = Array.from(dataMap.keys());
      const chartStartTs = props.chartStartDate ? getDayTimestamp(props.chartStartDate) : null;
      const chartEndTs = props.chartEndDate ? getDayTimestamp(props.chartEndDate) : null;
      const rangeStart = chartStartTs ?? (knownTimestamps.length > 0 ? Math.min(...knownTimestamps) : null);
      const rangeEnd = chartEndTs ?? (knownTimestamps.length > 0 ? Math.max(...knownTimestamps) : null);
      if (rangeStart !== null && rangeEnd !== null) {
        const dayMs = 24 * 60 * 60 * 1000;
        for (let ts = rangeStart; ts <= rangeEnd; ts += dayMs) {
          if (!dataMap.has(ts)) {
            const date = new Date(ts);
            dataMap.set(ts, {
              x: date,
              timestamp: ts,
              xLabel: formatMonthLabel(date),
            });
            originalDataMap.set(ts, { timestamp: ts });
          }
        }
      }
    }

    // Convert map to sorted array
    const sortedData = Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);

    return { sortedData, originalDataMap };
  }, [props.sanitaryWater, props.pluvioData, props.piezoData, props.mode, props.chartStartDate, props.chartEndDate]);

  // Reset any active zoom selection whenever the underlying data changes (new search, new range)
  React.useEffect(() => {
    setZoomDomain(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, [props.sanitaryWater, props.pluvioData, props.piezoData, props.chartStartDate, props.chartEndDate]);

  // Scroll-to-zoom (equivalent to the previous library's default VictoryZoomVoronoiContainer
  // behavior): mouse wheel over the daily chart zooms in/out, centered on the currently visible
  // range. A ref mirrors the zoom state so the native listener always reads the latest value
  // without needing to be re-attached on every zoom step.
  const zoomDomainRef = React.useRef(zoomDomain);
  React.useEffect(() => {
    zoomDomainRef.current = zoomDomain;
  }, [zoomDomain]);

  const fullRange = React.useMemo(() => {
    if (sortedData.length === 0) return null;
    return { min: sortedData[0].timestamp, max: sortedData[sortedData.length - 1].timestamp };
  }, [sortedData]);

  React.useEffect(() => {
    if (props.mode !== 'area') return;
    const host = chartRef.current;
    if (!host || !fullRange) return;

    const dayMs = 24 * 60 * 60 * 1000;

    const handleWheel = (wheelEvent: WheelEvent) => {
      wheelEvent.preventDefault();
      const fullSpan = fullRange.max - fullRange.min;
      if (fullSpan <= 0) return;

      const currentLeft = zoomDomainRef.current?.left ?? fullRange.min;
      const currentRight = zoomDomainRef.current?.right ?? fullRange.max;
      const span = currentRight - currentLeft;
      const center = (currentLeft + currentRight) / 2;

      // Scroll up/forward zooms in, scroll down/backward zooms out
      const zoomFactor = wheelEvent.deltaY < 0 ? 0.8 : 1.25;
      const minSpan = Math.min(2 * dayMs, fullSpan);
      const newSpan = Math.min(Math.max(span * zoomFactor, minSpan), fullSpan);

      let newLeft = center - newSpan / 2;
      let newRight = center + newSpan / 2;
      if (newLeft < fullRange.min) {
        newLeft = fullRange.min;
        newRight = newLeft + newSpan;
      }
      if (newRight > fullRange.max) {
        newRight = fullRange.max;
        newLeft = newRight - newSpan;
      }

      if (newSpan >= fullSpan) {
        setZoomDomain(null);
      } else {
        setZoomDomain({ left: newLeft, right: newRight });
      }
    };

    host.addEventListener('wheel', handleWheel, { passive: false });
    return () => host.removeEventListener('wheel', handleWheel);
  }, [props.mode, fullRange]);

  const monthlyBarData = sortedData;

  const numericStepReferenceFlow = Number(props.stepReferenceFlow);
  const hasReferenceFlow =
    Number.isFinite(numericStepReferenceFlow) && props.stepReferenceFlow !== null;
  const sanitarySeriesNames = React.useMemo(
    () =>
      (props.sanitaryWater ?? []).map((series: any[], index: number) => {
        const explicitType = (series ?? []).find((point: any) => point?.type)?.type;
        return explicitType || `Series ${index + 1}`;
      }),
    [props.sanitaryWater],
  );
  const controlData =
    (props.sanitaryWater !== null && props.sanitaryWater.length > 0) ||
    (props.pluvioData !== null && props.pluvioData.length > 0) ||
    (props.piezoData !== undefined && props.piezoData !== null && props.piezoData.length > 0);
  const showPluvioAxis = props.dataSelected?.id === 1 && props.pluvioData && props.pluvioData.length > 0;
  const showPiezoAxis = props.dataSelected?.id === 2 && props.piezoData && props.piezoData.length > 0;
  const showSecondaryAxis = showPluvioAxis || showPiezoAxis;
  const secondaryAxisLabel = showPluvioAxis ? 'Pluvio (mm)' : 'Piezo (m)';
  const secondaryAxisMax = showPluvioAxis
    ? props.pluvioMax
    : (props.piezoMax ?? 0);
  const referenceLegendLabel = `Débit de référence: ${numericStepReferenceFlow} m³/j`;

  // Series shown in the tooltip - always includes all sanitary series plus the
  // currently selected secondary series (pluvio/piezo), regardless of null values
  // Names are unit-free here since the displayed value already appends the unit
  const tooltipSeriesConfig = React.useMemo(() => {
    const config = (props.sanitaryWater ?? []).map((_: any, index: number) => ({
      dataKey: `series_${index}`,
      name: sanitarySeriesNames[index] ?? `Series ${index + 1}`,
      color: props.colorsLegend[index],
    }));
    if (showPluvioAxis) {
      config.push({ dataKey: 'pluvio', name: 'Pluvio', color: '#63D1B5' });
    }
    if (showPiezoAxis) {
      config.push({ dataKey: 'piezo', name: 'Piezo', color: '#9400D3' });
    }
    return config;
  }, [props.sanitaryWater, sanitarySeriesNames, props.colorsLegend, showPluvioAxis, showPiezoAxis]);

  // Create a tooltip wrapper that has access to original data
  const tooltipRenderer = (tooltipProps: any) =>
    CustomTooltip(tooltipProps, tooltipSeriesConfig, originalDataMap, props.mode);

  if (props.mode === 'area') {
    return (
      <div
        style={{
          display: props.display === 'none' ? 'none' : 'flex',
          height: props.height,
          width: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          flexDirection: 'column',
          borderRadius: '15px',
          overflow: 'hidden',
          boxShadow: '0 14px 14px 3px rgba(18, 26, 49, 0.05)',
          backgroundColor: 'white',
          marginBottom: '32px',
          paddingBottom: '0px',
        }}
      >
        {controlData && (
          <div
            style={{
              position: 'absolute',
              zIndex: 1,
            }}
          >
            {zoomButton(props)}
          </div>
        )}
        <div style={{ textAlign: 'center', fontSize: '16px', paddingTop: '15px', position: 'relative' }}>
          <Word value={props.title} variant="span" textStyle="normalBold" />
          {zoomDomain && (
            <span
              onClick={resetZoom}
              onKeyDown={(keyEvent) => {
                if (keyEvent.key === 'Enter' || keyEvent.key === ' ') resetZoom();
              }}
              role="button"
              tabIndex={0}
              title="Réinitialiser le zoom"
              style={{
                position: 'absolute',
                right: '16px',
                top: '13px',
                fontSize: '12px',
                color: '#243977',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Réinitialiser le zoom
            </span>
          )}
        </div>
        <div
          ref={chartRef}
          style={{
            position: 'relative',
            flex: '1 1 auto',
            minHeight: 0,
            width: '100%',
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={sortedData}
              margin={{ top: 24, right: 50, left: 20, bottom: 40 }}
              onMouseDown={handleChartMouseDown}
              onMouseMove={handleChartMouseMove}
              onMouseUp={handleChartMouseUp}
              style={{ cursor: 'crosshair' }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="x"
                type="number"
                scale="time"
                domain={zoomDomain ? [zoomDomain.left, zoomDomain.right] : ['dataMin', 'dataMax']}
                allowDataOverflow
                tickCount={8}
                interval="preserveStartEnd"
                minTickGap={24}
                tick={{ fontSize: 11, fill: '#000000' }}
                tickFormatter={(tick) => {
                  const date = new Date(tick);
                  return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'numeric',
                  });
                }}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: '#000000' }}
                label={{ value: props.axisYLabel, angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              {showSecondaryAxis && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, secondaryAxisMax > 0 ? secondaryAxisMax : 'auto']}
                  label={{ value: secondaryAxisLabel, angle: 90, position: 'insideRight', fontSize: 12 }}
                  tick={{ fontSize: 11, fill: '#000000' }}
                />
              )}
              <Tooltip content={tooltipRenderer} />
              <Legend
                verticalAlign="top"
                content={(legendProps: any) => (
                  <DailyLegendWithReference
                    payload={legendProps?.payload}
                    showReference={hasReferenceFlow}
                    referenceLabel={referenceLegendLabel}
                  />
                )}
              />
              {props.sanitaryWater &&
                props.sanitaryWater.map((_, index) => (
                  <Area
                    key={`area-${index}`}
                    type="monotone"
                    dataKey={`series_${index}`}
                    yAxisId="left"
                    stackId="sanitary"
                    stroke={props.colorsLegend[index]}
                    fill={props.colorsLegend[index]}
                    fillOpacity={0.78}
                    name={sanitarySeriesNames[index] ?? `Series ${index + 1}`}
                    connectNulls={false}
                  />
                ))}
              {props.dataSelected?.id === 1 && props.pluvioData && props.pluvioData.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="pluvio"
                  yAxisId="right"
                  stroke="#63D1B5"
                  strokeWidth={3}
                  name="Pluvio"
                  dot={false}
                />
              )}
              {props.dataSelected?.id === 2 && props.piezoData && props.piezoData.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="piezo"
                  yAxisId="right"
                  stroke="#9400D3"
                  strokeWidth={3}
                  name="Piezo"
                  dot={false}
                />
              )}
              {hasReferenceFlow && (
                <ReferenceLine
                  yAxisId="left"
                  y={numericStepReferenceFlow}
                  stroke="#E63946"
                  strokeWidth={2}
                  strokeDasharray="10,6"
                />
              )}
              {refAreaLeft !== null && refAreaRight !== null && (
                <ReferenceArea
                  yAxisId="left"
                  x1={refAreaLeft}
                  x2={refAreaRight}
                  strokeOpacity={0.3}
                  fill="#243977"
                  fillOpacity={0.15}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  } else {
    // Bar mode
    return (
      <div
        style={{
          display: props.display === 'none' ? 'none' : 'flex',
          height: props.height,
          width: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          flexDirection: 'column',
          borderRadius: '15px',
          overflow: 'hidden',
          boxShadow: '0 14px 14px 3px rgba(18, 26, 49, 0.05)',
          backgroundColor: 'white',
          marginBottom: '32px',
          paddingBottom: '0px',
        }}
      >
        {controlData && (
          <div
            style={{
              position: 'absolute',
              zIndex: 1,
            }}
          >
            {zoomButton(props)}
          </div>
        )}
        <div style={{ textAlign: 'center', fontSize: '16px', paddingTop: '15px' }}>
          <Word value={props.title} variant="span" textStyle="normalBold" />
        </div>
        <div
          ref={chartRef}
          style={{
            position: 'relative',
            flex: '1 1 auto',
            minHeight: 0,
            width: '100%',
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyBarData} margin={{ top: 24, right: 50, left: 40, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="xLabel"
                type="category"
                padding="gap"
                tick={<RotatedXAxisTick />}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: '#000000' }}
                label={{ value: props.axisYLabel, angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              {showSecondaryAxis && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, secondaryAxisMax > 0 ? secondaryAxisMax : 'auto']}
                  label={{ value: secondaryAxisLabel, angle: 90, position: 'insideRight', fontSize: 12 }}
                  tick={{ fontSize: 11, fill: '#000000' }}
                />
              )}
              <Tooltip content={tooltipRenderer} />
              <Legend verticalAlign="top" wrapperStyle={{ fontSize: 12 }} />
              {props.sanitaryWater &&
                props.sanitaryWater.map((_, index) => (
                  <Bar
                    key={`bar-${index}`}
                    dataKey={`series_${index}`}
                    yAxisId="left"
                    stackId="sanitary"
                    fill={props.colorsLegend[index]}
                    name={sanitarySeriesNames[index] ?? `Series ${index + 1}`}
                    barSize={40}
                  />
                ))}
              {props.dataSelected?.id === 1 && props.pluvioData && props.pluvioData.length > 0 && (
                <Bar yAxisId="right" dataKey="pluvio" fill="#63D1B5" name="Pluvio" barSize={40} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
};

export default React.memo(EcpChartsComponentRecharts);
